import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "./context/AuthContext";
import { Progress } from "@shadcn-in-nx/ui/progress";

const ProtectedRoute = ({ children, title }) => {
    const { isLoadingUser, user } = useAuth();
    const location = useLocation();
    const [progress, setProgress] = React.useState(0);
    const intervalRef = React.useRef(null);
    const isLoadingRef = React.useRef(isLoadingUser);

    // Keep ref in sync with loading state
    React.useEffect(() => {
        isLoadingRef.current = isLoadingUser;
    }, [isLoadingUser]);

    // Animate progress bar from 0 to 100% while loading
    React.useEffect(() => {
        // Clear any existing interval first
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        if (!isLoadingUser) {
            setProgress(0);
            return;
        }

        // Start at 0
        setProgress(0);

        // Animate to 100% over time
        intervalRef.current = setInterval(() => {
            setProgress((prev) => {
                // Check if we've reached 100% or loading stopped (using ref to avoid stale closure)
                if (prev >= 100 || !isLoadingRef.current) {
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    return Math.min(prev, 100);
                }
                // Slow down as we approach 100%
                const increment = prev < 50 ? 2 : prev < 90 ? 1 : 0.5;
                return Math.min(prev + increment, 100);
            });
        }, 50);

        // Cleanup function - always runs when effect re-runs or component unmounts
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [isLoadingUser]);

    if (!user && !isLoadingUser) {
        // Redirect to login while saving the attempted location
        return <Navigate to="/" state={{ from: location.pathname }} replace />;
    }

    return (
        <>
            {title && (
                <Helmet>
                    <title>{`${title} - AgentUI`}</title>
                </Helmet>
            )}
            {isLoadingUser && (
                <div className="fixed top-0 left-0 right-0 z-50">
                    <Progress value={progress} className="h-1" />
                </div>
            )}
            {children}
        </>
    );
};

export default ProtectedRoute;
